import { Typography, useEventCallback } from '@mui/material';
import FilterNotificationsAnscOperator from 'civil-status/components/research-notifications-side/filter';
import { ResearchNotificationsOperatorCrumbs } from 'civil-status/constants/breadcrumb';
import { CriteriRicercaNotifiche } from 'civil-status/model/notifications';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import TableNotificationsAnscOperator from 'civil-status/components/research-notifications-side/table';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import {
  FilterResearchNotificationsSide,
  NotificationsListSide,
} from 'civil-status/view-models/notifications-list-side';
import { headCells } from './constants';
import { NotificationsListAnscOperator } from 'civil-status/view-models/notifications-list-ansc-operator';
import { transformNotificationsListAnscOperator } from 'civil-status/utils/transformer-notifications-list-ansc-operator';
import { NotificationAnscOperator } from 'civil-status/model/notification-ansc-operator';
import { transformNotificationsResearchAnscOperator } from 'civil-status/utils/transform-notifications-research-ansc-operator';
import TitleStep from 'civil-status/components/shared/title-step';
import { noop } from 'shell/utils/noop';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { toast } from 'react-toastify';

const ResearchNotificationsAnscOperatorPage = (): ReactElement => {
  const params = useParams();

  const nameCat = params.valueCat || undefined;
  const idCat = params.idCat || undefined;

  const filterTable = useFilterTable<FilterResearchNotificationsSide>();

  // filtri atto
  const [openNotifications, setOpenNotifications] = useState<boolean>(true);

  // filtri selezionati
  const [filterSelected, setFilterSelected] = useState<CriteriRicercaNotifiche | undefined>({});

  // stato per controllare la tabella
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const confirmMassively = useHttpClient(`${__ACT_API_URL__}/notifiche/ricerca-operatore`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const [notificationsList, getNotificationsList] = useHttpFetch<
    HttpResponse<NotificationAnscOperator>,
    Array<NotificationsListAnscOperator>
  >(`${__ACT_API_URL__}/notifiche/ricerca-operatore`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => {
      const firstMapResponse = transformNotificationsListAnscOperator(response);
      return firstMapResponse.map((item) => ({
        ...item,
        description: (
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {item.description}
          </Typography>
        ),
      }));
    },
  });

  const getNotificationsListData = useEventCallback(
    (dataFilter?: CriteriRicercaNotifiche, page?: number, rowsPerPage?: number) => {
      (
        getNotificationsList({
          data: {
            criteriRicercaNotifiche: {
              ...(dataFilter?.criteriRicercaNotifiche || {}),
              confermaMassiva: false,
            },
            datiPaginazione: {
              numPagina: page || 0,
              numRecordPagina: rowsPerPage || 10,
            },
          },
        }) as ResponsePromise<
          HttpResponse<NotificationAnscOperator>,
          Array<NotificationsListAnscOperator>
        >
      ).then(() => {
        // salvo i filtri dell'ultima ricerca
        setFilterSelected(dataFilter);
      });
    }
  );

  const onConfirmMassively = useEventCallback((dataFilter?: CriteriRicercaNotifiche) => {
    (
      confirmMassively({
        data: {
          ...dataFilter,
          datiPaginazione: {
            numPagina: tableProps.page || 0,
            numRecordPagina: tableProps.rowsPerPage || 10,
          },
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      getNotificationsListData(filterSelected, tableProps.page || 0, tableProps.rowsPerPage || 1);
    });
  });

  const handleSubmitResearchNotifications = useEventCallback(
    (values: FilterResearchNotificationsSide, page?: number, rowsPerPage?: number) => {
      // mantenere dati
      filterTable.setValues(values);

      const dataFilter: CriteriRicercaNotifiche =
        transformNotificationsResearchAnscOperator(values);

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getNotificationsListData(dataFilter);
    }
  );

  useEffect(() => {
    if (((notificationsList.data as Array<NotificationsListSide>) || []).length) {
      // se abbiamo risultato chiudere accordion dei filtri
      setOpenNotifications(false);
    }
  }, [notificationsList.data]);

  useEffect(() => {
    if (filterTable.values?.tableProps) {
      handleSubmitResearchNotifications(
        filterTable.values,
        filterTable.values.tableProps?.page,
        filterTable.values.tableProps?.rowsPerPage
      );
    }
  }, []);

  const setTablePropsFilterTable = useEventCallback((page?: number, rowsPerPage?: number) => {
    filterTable.setValues({
      ...filterTable.values,
      tableProps: {
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      },
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={ResearchNotificationsOperatorCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <TitleStep
          onGetDataSaved={noop}
          title={`Gestione notifiche ANSC ${nameCat ? `- ${nameCat}` : ''}`}
          withoutAdvancedEditing
          isWithButtonNotifications
        />
      </div>
      <FilterNotificationsAnscOperator
        openNotifications={openNotifications}
        setOpenNotifications={setOpenNotifications}
        handleSubmitResearchNotifications={handleSubmitResearchNotifications}
        idCat={idCat}
        initialValues={filterTable.values}
        visibleFilters={{
          idEventNotification: false,
          scope: true,
          municipality: true,
          annotationProposal: true,
        }}
      />
      {notificationsList.data ? (
        ((notificationsList.data as Array<NotificationsListAnscOperator>) || [])?.length > 0 ? (
          <>
            <TableNotificationsAnscOperator
              data={notificationsList.data as Array<NotificationsListAnscOperator>}
              filterSelected={filterSelected}
              getNotificationsListData={getNotificationsListData}
              onConfirmMassively={onConfirmMassively}
              isWithConfirmMassively
              idCat={idCat}
              nameCat={nameCat}
              tableProps={tableProps}
              headCells={headCells}
              setTableProps={setTableProps}
              setTablePropsFilterTable={setTablePropsFilterTable}
              minWidth={2000}
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ResearchNotificationsAnscOperatorPage.displayName =
  'Research Notifications Ansc Operator Page component';

export default ResearchNotificationsAnscOperatorPage;
